import React, { useMemo, useRef } from "react";
import { connect } from "react-redux";
import * as actions from "../../store/actions";
import { withStyles } from "tss-react/mui";
import JamezzFormStyles from "../../views/JamezzForm/JamezzFormStyles";
import SubsSubscriptionGeneral from "./Tabs/SubsSubscriptionGeneral";
import TranslationWrapper, { getTranslation } from "../../components/TranslationWrapper/TranslationWrapper";
import { DialogContext, useDialog } from "../../licensing/utils/DialogProvider";
import AuthProvider from "../../auth/AuthProvider";
import JamezzFormV2 from "../JamezzForm/JamezzFormV2";
import { formatTitle } from "../../../utils/formatting";
import Button from "../../components/CustomButtons/Button";
import NewSubscriptionLine from "./Dialogs/NewSubscriptionLine";
import { Add, Edit } from "@mui/icons-material";
import JamezzTable from "../../components/JamezzTable/JamezzTable";
import { JamezzTableDateField, JamezzTableValuta } from "../../components/JamezzTable/JamezzTableFields";
import ControlledInput from "../../components/CustomInput/ControlledInput";
import CloseIcon from "@mui/icons-material/Close";
import EndSubscription from "./Dialogs/EndSubscription";
import EditSubscriptionLine from "./Dialogs/EditSubscriptionLine";
import SubsSubscriptionAdditional from "./Tabs/SubsSubscriptionAdditional";
import { useParams } from "react-router";

function SubsSubscriptionFormV2() {
  const endPoint = useMemo(() => {
    return "core/subsSubscription";
  }, []);
  const [openDialog] = useDialog();
  const jamezzTable = useRef(null);
  const params = useParams();

  const actions = [
    {
      icon: CloseIcon,
      tooltip: <TranslationWrapper tKey={"End license"} />,
      action: (row) => {
        openDialog({
          children: <EndSubscription subscriptionLineId={row[0]} />,
          fullWidth: true,
          maxWidth: "lg",
        });
      },
      show: (row) => {
        return row[4] == null;
      },
    },
    {
      icon: Edit,
      tooltip: <TranslationWrapper tKey={"Edit"} />,
      action: (row) => {
        openDialog({
          children: (
            <EditSubscriptionLine
              onSaved={() => {
                jamezzTable.current?.refreshData();
              }}
              subscriptionLineId={row[0]}
            />
          ),
          fullWidth: true,
          maxWidth: "md",
        });
      },
    },
  ];

  return (
    <AuthProvider prefix={"vestigingen"}>
      {params.id > 0 ? (
        <>
          <Button
            color={"success"}
            onClick={() => {
              openDialog({
                children: (
                  <NewSubscriptionLine
                    onSaved={() => {
                      jamezzTable.current?.refreshData();
                    }}
                    subscriptionId={params.id}
                  />
                ),
                fullWidth: true,
                maxWidth: "md",
              });
            }}
          >
            <Add />
            <TranslationWrapper tKey={"New license line"} />
          </Button>
          <JamezzTable
            actions={actions}
            dataMapping={[
              { dbfield: "id", editable: false },
              { dbfield: "type", editable: false },
              new JamezzTableDateField(
                "start_date",
                true,
                true,
                null,
                (r) => {
                  return r;
                },
                function (r, key, rowKey, rowProp) {
                  if (r.next_date_unix !== r.start_date_unix) {
                    return r.start_date;
                  }
                  return (
                    <ControlledInput
                      name={null}
                      nullable={false}
                      onHandleValue={(value) => {
                        this.updateData(rowKey, key, "start_date", rowProp[0], value);
                      }}
                      type={"date"}
                      value={r.start_date}
                    />
                  );
                }
              ),
              new JamezzTableDateField(
                "next_date",
                true,
                false,
                null,
                (r) => {
                  return r.end_date_unix == null || r.end_date_unix > r.next_date_unix ? r.next_date : null;
                },
                function (r, key, rowKey, rowProp) {
                  if (r == null) {
                    return "-";
                  }
                  return (
                    <ControlledInput
                      name={null}
                      nullable={false}
                      onHandleValue={(value) => {
                        this.updateData(rowKey, key, "next_date", rowProp[0], value);
                      }}
                      type={"date"}
                      value={r}
                    />
                  );
                }
              ),
              new JamezzTableDateField("end_date", false, true),
              { dbfield: "count", editable: true, inputtype: "ControlledInput" },
              new JamezzTableValuta("unit_price", true, true),
              new JamezzTableValuta("discount_amount", true, true),
            ]}
            dbfields={
              "id,description,type,count,start_date,start_date_unix,next_date,next_date_unix,end_date,end_date_unix,status,status_invoicing,unit_price"
            }
            delete={true}
            fetchUrl={"core/subsSubscriptionLine"}
            filters={[
              {
                field: "subs_subscription_id",
                operator: "=",
                value: params.id,
              },
            ]}
            headerIcon={null}
            headers={[
              {
                label: getTranslation("#"),
                sortable: true,
                sortKey: "id",
              },
              {
                label: getTranslation("Type"),
              },
              {
                label: getTranslation("Start Date"),
                sortable: true,
                sortKey: "start_date_unix",
              },
              {
                label: getTranslation("Next Invoice Date"),
              },
              {
                label: getTranslation("End Date"),
              },
              {
                label: getTranslation("Quantity"),
              },
              {
                label: getTranslation("Overwrite") + " " + getTranslation("Unit price"),
              },
              {
                label: getTranslation("Overwrite") + " " + getTranslation("Discount"),
              },
            ]}
            reduxKey={"subsSubscriptionLines"}
            ref={jamezzTable}
            tableTitle={<TranslationWrapper tKey={"License details"} />}
          />
        </>
      ) : null}
      <JamezzFormV2
        computedPageTitle={function (model) {
          return formatTitle(this.pageTitle, model?.venue?.name);
        }}
        endPoint={endPoint}
        excludedKeysInOutput={{}}
        extraFormProps={{}}
        mode={"onSubmit"}
        objectsExcludedAsRelation={{ subs_price_plans: true }}
        pageTitle={getTranslation("License")}
        pageTitleSubject={<small></small>}
        parseInput={{
          invoice_period: (response) => JSON.stringify(response),
          subs_price_plans: (response, defaultValues) => {
            defaultValues.subs_price_plans__ids = JSON.stringify(response.map((obj) => obj.id));
            return response;
          },
        }}
        parseOutput={{
          subs_price_plans__ids: (value, defaultValue, dirtyData) => {
            const ids = JSON.parse(value);
            if (ids) {
              ids.forEach((id) => {
                dirtyData.append("subs_price_plans__ids[]", id);
              });
            }

            return undefined;
          },
        }}
        revalidateMode={"onChange"}
        tabs={[
          {
            tabButton: <TranslationWrapper tKey={"General"} />,
            tabContent: <SubsSubscriptionGeneral />,
          },
          {
            tabButton: <TranslationWrapper tKey={"Additional items to be invoiced"} />,
            tabContent: <SubsSubscriptionAdditional />,
          },
        ]}
        rootRight={"vestigingen"}
        // extraFields={extraFields}
        onGetModel={(model) => {
          // onGetModel(model);
        }}
      />
    </AuthProvider>
  );
}

//REDUX
const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    showMessage: (message) => dispatch({ type: actions.SHOW_MESSAGE, message: message }),
  };
};

SubsSubscriptionFormV2.contextType = DialogContext;

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(SubsSubscriptionFormV2, JamezzFormStyles));
